import { DeductionExt, DeductionUpdate, get_updates, UpdateAction } from "@/api/deduction.tsx"
import { complete_task, DeductionTask, TaskInfo, TaskStatus, TaskType } from "@/api/task.tsx"
import { get_display_name, useAsyncEffect } from "@/utils"
import { capitalCase } from "change-case"
import { format, parseISO } from "date-fns"
import { useState } from "preact/hooks"
import useLocation from "wouter-preact/use-location"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { copyOnAltClick } from "@/deduction_table/util.tsx"
import { closeModalAndNavigate } from "@/deduction/deduction_detail_modal_state"

export function formatTaskType(taskType: TaskType) {
  switch (taskType) {
    case TaskType.FileDispute:
      return "File dispute"
    case TaskType.PullBackup:
      return "Pull backup"
    case TaskType.Followup:
      return "Follow up"
    case TaskType.Validate:
      return "Validate deduction"
    case TaskType.Expense:
      return "Expense"
    case TaskType.MapAccountingCode:
      return "Map accounting code"
    case TaskType.UpdateDcPo:
      return "Update DC/PO"
  }
}

/**
 * Human-readable reminder trail for a task, e.g. "2 reminders sent (last Aug 10)". Empty when no
 * reminders have gone out yet (or the task JSON predates reminder counting).
 */
export function format_reminders_sent(bump_count?: number | null, last_bump_at?: string | null): string {
  if (!bump_count || bump_count <= 0) {
    return ""
  }
  const last = last_bump_at ? ` (last ${format(parseISO(last_bump_at), "MMM d")})` : ""
  return `${bump_count} reminder${bump_count === 1 ? "" : "s"} sent${last}`
}

async function handleTaskComplete(task_id: string) {
  const res = await complete_task(task_id)
  if (!res.ok) {
    console.error("Failed to mark task complete")
  }
  RefreshFlag.set(flag => !flag)
}

/** The task fields the timeline renders, common to the mirrored `deduction.task` JSON and the full `tasks` list. */
interface TaskUpdateSource {
  id: string
  status: TaskStatus
  type: TaskType
  created_at?: string | null
  user_display_name?: string | null
  user_email?: string | null
  assigner_display_name?: string | null
  assigner_email?: string | null
  backup_type?: string | null
  note?: string | null
  bump_count?: number | null
  last_bump_at?: string | null
}

function build_task_update(task: TaskUpdateSource) {
  let action: UpdateAction | undefined
  if (task.status === TaskStatus.Pending) {
    action = {
      type: "mark_complete",
      title: "Mark complete",
      task_id: task.id,
    }
  }
  return {
    date: task.created_at ?? "",
    title: `${capitalCase(task.status ?? "")} task`,
    description: `${task.user_display_name ?? get_display_name(task.user_email) ?? ""} assigned to ${formatTaskType(task.type)}${
      task.backup_type ? ` (${task.backup_type.toLowerCase()})` : ""
    }${task.note ? `\nNote: ${task.note}` : ""}${
      format_reminders_sent(task.bump_count, task.last_bump_at) ? `\n${format_reminders_sent(task.bump_count, task.last_bump_at)}` : ""
    }`,
    actor: task.assigner_display_name ?? get_display_name(task.assigner_email) ?? "",
    action,
  }
}

/**
 * One timeline entry per task. Detail payloads carry every task in `tasks` (so a deduction with
 * several pending tasks shows all of them); elsewhere only the single mirrored `task` JSON exists.
 */
export function task_update_entries(deduction: { tasks?: DeductionTask[]; task?: TaskInfo }): DeductionUpdate[] {
  if (deduction.tasks) {
    return deduction.tasks.map((task: DeductionTask) => build_task_update({ ...task, type: task.task_type }))
  }
  if (deduction.task?.created_at) {
    return [build_task_update(deduction.task)]
  }
  return []
}

interface UpdatesProps {
  deduction: DeductionExt & { tasks?: DeductionTask[] }
  className?: string
}

export function Timeline2({ deduction, className }: UpdatesProps) {
  let [serverUpdates, setUpdates] = useState<DeductionUpdate[]>([])
  let [, setLocation] = useLocation()

  useAsyncEffect(async () => {
    let res = await get_updates(deduction.id)
    if (!res.ok) {
      console.error(res.error)
      return
    }
    setUpdates(res.value)
  })

  const updates: DeductionUpdate[] = [...task_update_entries(deduction), ...serverUpdates]
  updates.sort((a, b) => a.date.localeCompare(b.date))

  if (!updates.length) {
    return null
  }
  return (
    <div class={className}>
      <h3 class="text-lg font-semibold">Timeline</h3>
      <div class="flex flex-col gap-4 mt-4">
        {updates.map((update, i) => {
          let action_el
          if (update.action) {
            let handler
            let action = update.action
            if (action.type === "mark_complete") {
              handler = () => handleTaskComplete(action.task_id)
            } else if (action.type === "open_link") {
              handler = () => closeModalAndNavigate(setLocation, action.url)
            } else {
              throw new Error("Unknown action type")
            }
            action_el = (
              <button
                onClick={handler}
                class="border border-blue-500 text-blue-500 px-2 py-1 rounded text-sm -mt-1 cursor-pointer whitespace-nowrap">
                {action.title}
              </button>
            )
          }
          let title = <div class="text-md">{update.title}</div>
          if ((update.title === "Dispute created" || update.title === "Backup requested") && deduction.dispute_id) {
            title = (
              <div class="text-md" onClick={copyOnAltClick(deduction.dispute_id)}>
                {update.title}
              </div>
            )
          }
          return (
            <div class="flex gap-2 items-start">
              <div class="grow">
                {title}
                <span class="text-gray-500 text-sm">{update.description}</span>
              </div>
              {action_el}
              <div class="flex items-center">
                <div class="text-sm text-gray-500 text-right whitespace-nowrap">
                  {format(parseISO(update.date), "PP")}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("task_update_entries", () => {
    function make_task(overrides: Partial<DeductionTask>): DeductionTask {
      return {
        id: "task-1",
        org_id: "org-1",
        user_id: "user-1",
        user_display_name: "Jane Doe",
        assigner_id: "user-2",
        assigner_display_name: "John Smith",
        task_type: TaskType.PullBackup,
        status: TaskStatus.Pending,
        created_at: "2026-08-01T12:00:00Z",
        updated_at: "2026-08-01T12:00:00Z",
        bump_count: 0,
        ...overrides,
      }
    }

    it("renders one entry per task with a complete action only on pending tasks", () => {
      const entries = task_update_entries({
        tasks: [
          make_task({ id: "a", status: TaskStatus.Pending }),
          make_task({ id: "b", status: TaskStatus.Completed, task_type: TaskType.Validate, created_at: "2026-08-02T12:00:00Z" }),
        ],
      })
      expect(entries).toHaveLength(2)
      expect(entries[0].title).toBe("Pending task")
      expect(entries[0].action).toEqual({ type: "mark_complete", title: "Mark complete", task_id: "a" })
      expect(entries[0].description).toContain("Jane Doe assigned to Pull backup")
      expect(entries[1].title).toBe("Completed task")
      expect(entries[1].action).toBeUndefined()
    })

    it("prefers the full tasks list over the single mirrored task JSON", () => {
      const mirrored = {
        id: "old",
        type: TaskType.Followup,
        status: TaskStatus.Pending,
        user_id: "user-1",
        created_at: "2026-08-01T12:00:00Z",
        assigner_email: "a@b.com",
        user_email: "c@d.com",
      }
      const entries = task_update_entries({ tasks: [make_task({ id: "new" })], task: mirrored })
      expect(entries).toHaveLength(1)
      expect(entries[0].action).toEqual({ type: "mark_complete", title: "Mark complete", task_id: "new" })
    })

    it("falls back to the mirrored task JSON when no tasks list is present", () => {
      const mirrored = {
        id: "old",
        type: TaskType.Followup,
        status: TaskStatus.Completed,
        user_id: "user-1",
        created_at: "2026-08-01T12:00:00Z",
        assigner_email: "a@b.com",
        user_email: "c@d.com",
      }
      expect(task_update_entries({ task: mirrored })).toHaveLength(1)
      expect(task_update_entries({})).toHaveLength(0)
    })
  })

  describe("format_reminders_sent", () => {
    it("is empty when no reminders have been sent or the field is absent", () => {
      expect(format_reminders_sent(0, null)).toBe("")
      expect(format_reminders_sent(undefined, undefined)).toBe("")
      expect(format_reminders_sent(null, "2026-08-10T12:00:00Z")).toBe("")
    })

    it("pluralizes and includes the last reminder date", () => {
      expect(format_reminders_sent(1, "2026-08-10T12:00:00Z")).toBe("1 reminder sent (last Aug 10)")
      expect(format_reminders_sent(3, "2026-08-10T12:00:00Z")).toBe("3 reminders sent (last Aug 10)")
    })

    it("omits the date when last_bump_at is missing", () => {
      expect(format_reminders_sent(2, null)).toBe("2 reminders sent")
    })
  })
}
